// src/components/leaderboard/LeaderboardPopup.js
//
// THE LEADERBOARD (step 4). Two tabs:
//
//   🏫 My class     — this week's coin earners in the student's class, with
//                     their avatars. Top three stand on a podium; everyone
//                     else is a row. "You" is highlighted. Shows when the
//                     board resets (Monday) and last week's champion.
//   🌎 All schools  — class totals, only for classes whose teacher switched
//                     it on. No student names ever leave the class.
//
// Teachers (isEducator) also get the switch for the all-schools board and
// can name their team.
//
// Props: code (class code), myUid, isEducator, onClose()

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useMemo, useState } from "react";
import { Coin } from "../coins/CoinCounter";
import { useAvatarLooks } from "../../services/avatarService";
import FocusAvatar, { clearFocusedAvatar } from "../avatar/FocusAvatar";
import {
  resetLabel,
  setLeagueOptIn,
  useClassBoard,
  useLeagueBoard,
  useLeagueSetting,
} from "../../services/leaderboardService";
import "./leaderboard.css";

const MEDALS = ["🥇", "🥈", "🥉"];

// Tap any avatar to see the whole body (one at a time; back to portraits
// when the board is opened again).
const Face = ({ who, size, id, name, fullSize }) => <FocusAvatar id={id} look={who?.look || null} outfit={who?.outfit || {}} size={size} fullSize={fullSize} name={name} />;

const Podium = ({ rows, looks, myId, meName = () => "You!" }) => {
  // order on screen: 2nd, 1st, 3rd
  const spots = [rows[1], rows[0], rows[2]];
  return (
    <div className="sbl-lb-podium">
      {spots.map((r, i) => {
        if (!r) return <div key={i} className="sbl-lb-spot is-empty" />;
        const step = i === 1 ? 1 : i === 0 ? 2 : 3;
        return (
          <div key={r.id} className={`sbl-lb-spot is-${step} ${r.id === myId ? "is-me" : ""}`}>
            <div className="sbl-lb-spot-who">
              {looks ? <Face who={looks[r.id]} id={r.id} name={r.name} size={step === 1 ? 92 : 74} fullSize={step === 1 ? 210 : 180} /> : <span className="sbl-lb-team-badge">{r.state || "🏫"}</span>}
              <span className="sbl-lb-medal">{MEDALS[r.place - 1] || r.place}</span>
            </div>
            <strong className="sbl-lb-spot-name">{r.id === myId ? meName(r) : r.name}</strong>
            <span className="sbl-lb-coins">
              <Coin size={16} /> {r.coins}
            </span>
            <div className="sbl-lb-step">{r.place}</div>
          </div>
        );
      })}
    </div>
  );
};

const Rows = ({ rows, looks, myId, meName = (r) => `${r.name} (you)` }) => (
  <ol className="sbl-lb-rows">
    {rows.map((r) => (
      <li key={r.id} className={r.id === myId ? "is-me" : ""}>
        <span className="sbl-lb-place">{r.place}</span>
        {looks ? <Face who={looks[r.id]} id={r.id} name={r.name} size={38} fullSize={170} /> : <span className="sbl-lb-team-badge is-small">{r.state || "🏫"}</span>}
        <span className="sbl-lb-name">
          {r.id === myId ? meName(r) : r.name}
          {!looks && r.grade ? <small> · {r.grade}</small> : null}
        </span>
        <span className="sbl-lb-coins">
          <Coin size={15} /> {r.coins}
        </span>
      </li>
    ))}
  </ol>
);

const LeaderboardPopup = ({ code, myUid, isEducator = false, onClose }) => {
  const [tab, setTab] = useState("class");
  const board = useClassBoard(code);
  const league = useLeagueBoard(tab === "league");
  const setting = useLeagueSetting(code);
  const [, tick] = useState(0);
  const [teamDraft, setTeamDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const lookIds = useMemo(() => [...board.rows.slice(0, 30).map((r) => r.uid), ...(board.lastWeek[0] ? [board.lastWeek[0].uid] : [])], [board.rows, board.lastWeek]);
  const looks = useAvatarLooks(lookIds);
  // every time the board opens, everyone starts as a portrait
  useEffect(() => {
    clearFocusedAvatar();
    return clearFocusedAvatar;
  }, []);

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60000);
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      clearInterval(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const me = board.rows.find((r) => r.uid === myUid);
  // the nearest student with more coins than me
  const ahead = me ? board.rows.filter((r) => r.coins > me.coins).pop() : null;
  const champ = board.lastWeek[0];
  const myTeam = league.rows.find((r) => r.id === board.teamId);

  const toggleLeague = async (on) => {
    setBusy(true);
    setMsg("");
    try {
      await setLeagueOptIn(on, (teamDraft ?? setting.team ?? "").trim());
      setMsg(on ? "Your class is on the all-schools board!" : "Your class is off the all-schools board.");
      setTeamDraft(null);
    } catch (e) {
      setMsg(e?.message || "Couldn't change that just now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalPortal>
      <div className="sbl-lb-pop" role="dialog" aria-modal="true" aria-label="Leaderboard" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="sbl-lb">
          <header className="sbl-lb-head">
            <h2>🏆 Leaderboard</h2>
            <span className="sbl-lb-reset" title="Everyone starts again at 0 on Monday">
              ⏳ New week in {resetLabel()}
            </span>
            <button type="button" className="sbl-lb-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <nav className="sbl-lb-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "class"} className={tab === "class" ? "is-on" : ""} onClick={() => setTab("class")}>
              🏫 My class
            </button>
            <button type="button" role="tab" aria-selected={tab === "league"} className={tab === "league" ? "is-on" : ""} onClick={() => setTab("league")}>
              🌎 All schools
            </button>
          </nav>

          <div className="sbl-lb-body">
            {tab === "class" && isEducator && code && (
              <div className="sbl-lb-teacher is-compact">
                <label className="sbl-lb-switch">
                  <input type="checkbox" checked={setting.on} disabled={busy || !setting.loaded} onChange={(e) => toggleLeague(e.target.checked)} />
                  <span className="sbl-lb-switch-ui" aria-hidden="true" />
                  <span>
                    All-schools board: <b>{setting.on ? "ON" : "OFF"}</b>
                    {setting.on && setting.team ? ` — as “${setting.team}”` : ""}
                  </span>
                </label>
                <button type="button" className="sbl-lb-linkbtn" onClick={() => setTab("league")}>
                  {setting.on ? "See all schools →" : "Name your team →"}
                </button>
                {msg && <p className="sbl-lb-small is-msg">{msg}</p>}
              </div>
            )}
            {tab === "class" ? (
              !code ? (
                <p className="sbl-lb-empty">Join a class with your teacher's code to see your class leaderboard.</p>
              ) : !board.loaded ? (
                <p className="sbl-lb-empty">Loading…</p>
              ) : board.error ? (
                <p className="sbl-lb-empty">The leaderboard isn't available right now.</p>
              ) : (
                <>
                  {board.rows.length === 0 ? (
                    <p className="sbl-lb-empty">
                      Nobody has earned coins this week yet.
                      <br />
                      Play a game to be first on the board! 🚀
                    </p>
                  ) : (
                    <>
                      <Podium rows={board.rows} looks={looks} myId={myUid} />
                      <p className="sbl-lb-tap">👆 Tap anyone's avatar to see their whole outfit</p>
                      {board.rows.length > 3 && <Rows rows={board.rows.slice(3)} looks={looks} myId={myUid} />}
                    </>
                  )}
                  {!isEducator && (
                    <p className="sbl-lb-me">
                      {me ? (
                        <>
                          You're <b>#{me.place}</b> with <Coin size={15} /> <b>{me.coins}</b> this week.
                          {ahead && ` ${ahead.coins - me.coins + 1} more to move up!`}
                        </>
                      ) : (
                        "Earn coins in games, videos, songs and Treasure Quest to get on the board!"
                      )}
                    </p>
                  )}
                  {champ && (
                    <div className="sbl-lb-champ">
                      <Face who={looks[champ.uid]} id={`champ-${champ.uid}`} size={44} fullSize={160} />
                      <span>
                        Last week's champion: <b>{champ.uid === myUid ? "You! 🎉" : champ.name}</b> with {champ.coins} coins
                      </span>
                      <span className="sbl-lb-crown" aria-hidden="true">
                        👑
                      </span>
                    </div>
                  )}
                </>
              )
            ) : (
              <>
                {isEducator && code && (
                  <div className="sbl-lb-teacher">
                    <label className="sbl-lb-switch">
                      <input type="checkbox" checked={setting.on} disabled={busy || !setting.loaded} onChange={(e) => toggleLeague(e.target.checked)} />
                      <span className="sbl-lb-switch-ui" aria-hidden="true" />
                      <span>Show my class on the all-schools board</span>
                    </label>
                    <div className="sbl-lb-team-row">
                      <input
                        type="text"
                        maxLength={40}
                        placeholder="Team name, e.g. Room 12 Rockets"
                        value={teamDraft ?? setting.team}
                        onChange={(e) => setTeamDraft(e.target.value)}
                      />
                      {teamDraft !== null && teamDraft.trim() !== setting.team && (
                        <button type="button" disabled={busy} onClick={() => toggleLeague(setting.on)}>
                          Save name
                        </button>
                      )}
                    </div>
                    <p className="sbl-lb-small">Only your team name, state, grade and class total are shown — never student names or your class code.</p>
                    {msg && <p className="sbl-lb-small is-msg">{msg}</p>}
                  </div>
                )}
                {!league.loaded ? (
                  <p className="sbl-lb-empty">Loading…</p>
                ) : league.rows.length === 0 ? (
                  <p className="sbl-lb-empty">No classes on the all-schools board this week yet.</p>
                ) : (
                  <>
                    <Podium rows={league.rows} looks={null} myId={board.teamId} meName={(r) => `${r.name} ⭐`} />
                    {league.rows.length > 3 && <Rows rows={league.rows.slice(3)} looks={null} myId={board.teamId} meName={(r) => `${r.name} ⭐ (your class)`} />}
                  </>
                )}
                {!isEducator && (
                  <p className="sbl-lb-me">
                    {myTeam
                      ? `Your class is #${myTeam.place} of ${league.rows.length}! Every coin you earn helps your class.`
                      : setting.loaded && !setting.on
                      ? "Your class isn't on the all-schools board. Ask your teacher to join!"
                      : "Every coin you earn helps your class climb."}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default LeaderboardPopup;
